import { useCallback, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { updateBoard } from "../../../../services/apiBoards";
import { useBoardUsers } from "../../../../reactQuery/users";
import { sortBoardUsers } from "../utils/boardManagementUtils";
import { useBoardMutationDialog } from "./useBoardMutationDialog";
import { getResponseData } from "../../../../reactQuery/response";

export function useBoardOwnerAssignment({ boardId, handleClose }) {
  const queryClient = useQueryClient();
  const [selectedUser, setSelectedUser] = useState(null);
  const { isLoading, error, data } = useBoardUsers(boardId);

  const users = useMemo(
    () => getResponseData(data, []),
    [data]
  );
  const sortedUsers = useMemo(() => sortBoardUsers(users), [users]);

  const ownerAssignmentDialog = useBoardMutationDialog({
    apiFn: updateBoard,
    invalidateKeys: ["boards"],
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["boardName", boardId],
      });
      handleClose?.();
      setSelectedUser(null);
    },
    onError: () => {
      setSelectedUser(null);
    },
  });

  const selectBoardOwner = useCallback(
    (user) => {
      setSelectedUser(user);
      ownerAssignmentDialog.openDialog();
    },
    [ownerAssignmentDialog]
  );

  const closeOwnerDialog = useCallback(() => {
    setSelectedUser(null);
    ownerAssignmentDialog.closeDialog();
  }, [ownerAssignmentDialog]);

  const submitOwnerChange = useCallback(() => {
    if (!selectedUser) {
      return;
    }

    ownerAssignmentDialog.submit({
      board_id: boardId,
      data: { owner_user_id: selectedUser.user_id },
    });
  }, [boardId, ownerAssignmentDialog, selectedUser]);

  return {
    isLoading,
    error,
    sortedUsers,
    selectedUser,
    isDialogOpen: ownerAssignmentDialog.isDialogOpen,
    isSubmitting: ownerAssignmentDialog.isSubmitting,
    selectBoardOwner,
    closeOwnerDialog,
    submitOwnerChange,
  };
}
